// MemoryPanel — cross-session memory CRUD. The agent reads/writes
// these directly via memory_read/memory_write tools; this panel is
// the user-facing list + manual edit + delete.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type Memory, type MemoryMeta, type MemoryType } from "../../lib/rpc";
import { confirm } from "../../components/confirm";
import { LibraryPanel, LibraryRow, LibraryEmpty } from "./library-panel";

const TYPES: MemoryType[] = ["user", "feedback", "project", "reference"];

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 64) || "memory";
}

export function MemoryPanel({
  open, onClose, notify,
}: {
  open: boolean;
  onClose: () => void;
  notify: (msg: string) => void;
}) {
  const [list, setList] = useState<MemoryMeta[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [draft, setDraft] = useState<Memory | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try { setList(await api.memoryList()); }
    catch (e: any) { notify(e?.message || "failed to load memories"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) refresh(); }, [open]);

  // Load body when active changes.
  useEffect(() => {
    if (!active) { setDraft(null); return; }
    let cancelled = false;
    api.memoryGet(active).then((m) => { if (!cancelled) setDraft(m); }).catch(() => {});
    return () => { cancelled = true; };
  }, [active]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await api.memorySave({
        name: draft.name,
        description: draft.description,
        type: draft.type,
        body: draft.body,
      });
      await refresh();
      notify("Memory saved");
      setActive(draft.name);
      setCreating(false);
    } catch (e: any) { notify(e?.message || "save failed"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!draft) return;
    const ok = await confirm({
      title: `Delete memory "${draft.name}"?`,
      message: "This memory entry will be removed from your cross-session notes.",
      confirmLabel: "Delete memory",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.memoryDelete(draft.name);
      notify("Deleted");
      await refresh();
      setDraft(null);
      setActive(null);
    } catch (e: any) { notify(e?.message || "delete failed"); }
  };

  const startNew = () => {
    setCreating(true);
    setActive(null);
    setDraft({
      name: "",
      description: "",
      type: "project",
      body: "",
      updatedAt: "",
    });
  };

  return (
    <LibraryPanel
      open={open}
      onClose={onClose}
      title="Memory"
      icon="memory"
      sub={`${list.length} entries · cross-session`}
      toolbar={
        <button className="icon-btn" onClick={startNew} title="New memory" type="button">
          <Icon name="plus" />
        </button>
      }
      list={
        loading
          ? <p className="lib-loading">loading…</p>
          : list.length === 0 && !creating
            ? <p className="lib-loading">No memories yet. Tap + to add one.</p>
            : list.map((m) => (
                <LibraryRow
                  key={m.name}
                  active={active === m.name}
                  name={m.name}
                  description={m.description}
                  badge={m.type}
                  onClick={() => { setActive(m.name); setCreating(false); }}
                />
              ))
      }
      detail={
        !draft
          ? <LibraryEmpty icon="memory" headline="Memory" sub="Pick a memory to view it, or tap + to add one." />
          : (
            <div className="lib-detail-pane">
              <div className="lib-detail-form">
                <div className="lib-field">
                  <label>Name</label>
                  <input
                    type="text"
                    value={draft.name}
                    disabled={!creating}
                    onInput={(e) => setDraft({ ...draft, name: slug((e.currentTarget as HTMLInputElement).value) })}
                    placeholder="my-memory"
                  />
                </div>
                <div className="lib-field">
                  <label>Type</label>
                  <div className="lib-type-pills">
                    {TYPES.map((t) => (
                      <button
                        key={t}
                        type="button"
                        data-active={draft.type === t}
                        onClick={() => setDraft({ ...draft, type: t })}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="lib-field">
                  <label>Description</label>
                  <input
                    type="text"
                    value={draft.description}
                    onInput={(e) => setDraft({ ...draft, description: (e.currentTarget as HTMLInputElement).value })}
                    placeholder="one-line hook"
                    maxLength={200}
                  />
                </div>
                <div className="lib-field">
                  <label>Body</label>
                  <textarea
                    rows={10}
                    value={draft.body}
                    onInput={(e) => setDraft({ ...draft, body: (e.currentTarget as HTMLTextAreaElement).value })}
                    placeholder="freeform markdown…"
                  />
                </div>
                <div className="lib-detail-actions">
                  {!creating && (
                    <button className="btn-ghost" type="button" onClick={remove}>Delete</button>
                  )}
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={save}
                    disabled={saving || !draft.name || !draft.description}
                  >
                    <Icon name="check" size={13} />
                    <span>{saving ? "Saving…" : "Save"}</span>
                  </button>
                </div>
              </div>
            </div>
          )
      }
    />
  );
}
